import React from "react";
import { Link as RouteLink, Navigate, useNavigate, useParams } from "react-router-dom";
import { Box, Grid } from "@mui/material";
import styled from "styled-components";
import { ChevronLeft } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import gitlab from "../../assets/icons/gitlab";
import github from "../../assets/icons/github";
import slack from "../../assets/icons/slack";
import jira from "../../assets/icons/jira";
import googleWorkspace from "../../assets/icons/google-workspace";
import Icon from "../../components/icon";
import Flex from "../../components/flex";
import microsoft from "../../assets/icons/microsoft";

const H1 = styled.h1`
    font-size: 22px;
    font-weight: 500;
    color: ${({ theme, success }) =>
        success ? theme.palette.primary.main : theme.palette.error.main};
`;

const Span = styled.span`
    font-size: 16px;
    font-weight: 300;
    opacity: 0.35;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const Link = styled(RouteLink)`
    font-weight: 500;
    font-size: 14px;
    margin: 0;
    color: ${({ theme }) => theme.palette.text.primary};
    text-decoration: none;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

const integrations = {
    slack: {
        icon: slack,
        name: "Slack",
    },
    gitlab: {
        icon: gitlab,
        name: "GitLab",
    },
    github: {
        icon: github,
        name: "GitHub",
    },
    jira: {
        icon: jira,
        name: "Jira",
    },
    google: {
        icon: googleWorkspace,
        name: "Google Workspace",
    },
    microsoft: {
        icon: microsoft,
        name: "Microsoft",
    },
};

const text = {
    success: "You will soon be redirected to your updated dashboard.",
    failure: "There was an error integrating with your provider",
    denied: "It appears that you canceled the integration",
};

const IntegrationResult = () => {
    const { response, integrationName } = useParams();
    const navigate = useNavigate();
    const success = response === "success";
    const denied = response === "denied";

    const cameFromWizard = localStorage.getItem("wizard");

    React.useEffect(() => {
        if (success && integrationName === "slack") {
            const timeout = cameFromWizard
                ? setTimeout(() => navigate("/setup-wizard"), 3500)
                : setTimeout(() => navigate("/dashboard/collaboration-analytics"), 3500);
            return () => clearTimeout(timeout);
        }
        if (success && integrationName === "google") {
            const timeout = cameFromWizard
                ? setTimeout(() => navigate("/setup-wizard"), 3500)
                : setTimeout(() => navigate("/dashboard/collaboration-analytics"), 3500);
            return () => clearTimeout(timeout);
        }
        if (success && integrationName === "notion") {
            const timeout = setTimeout(() => navigate("/dashboard/collaboration-analytics"), 3500);
            return () => clearTimeout(timeout);
        }
        if (success) {
            const urlsSuffix =
                integrationName.startsWith("git") || integrationName.startsWith("jira")
                    ? "work-analytics"
                    : "collaboration-analytics";
            const timeout = cameFromWizard
                ? setTimeout(() => navigate(`/setup-wizard`), 3500)
                : setTimeout(() => navigate(`/dashboard/${urlsSuffix}`), 3500);
            return () => clearTimeout(timeout);
        }
        return undefined;
    }, []);

    const integration = integrations[integrationName];
    if (!integration && cameFromWizard) return <Navigate to="/setup-wizard" />;
    if (!integration) return <Navigate to="/integrations" />;

    const getText = () => {
        if (success) return text.success;
        if (denied) return text.denied;
        return text.failure;
    };

    const getTitleText = () => {
        if (success) return "Successful";
        if (denied) return "Denied";
        return "Failed";
    };

    return (
        <>
            <Helmet>
                <title>
                    Adadot - {integration.name} Integration {getTitleText()}
                </title>
            </Helmet>
            <Box mt={10}>
                <Grid
                    container
                    spacing={3}
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Grid item>
                        <Icon svg={integration.icon} width="250px" height="auto" />
                    </Grid>
                    <Grid item style={{ textAlign: "center" }}>
                        <H1 success={success}>
                            Integration with {integration.name} {success ? "successful" : "failed"}
                        </H1>
                        <Span>{getText()}</Span>
                    </Grid>
                    {!success && (
                        <Grid item style={{ marginTop: "40px" }}>
                            <Link to="/integrations">
                                <Flex justifyContent="center">
                                    <ChevronLeft />
                                    Return to the integration panel
                                </Flex>
                            </Link>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </>
    );
};

export default IntegrationResult;
